import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  AmountInput,
  Button,
  FeeConfigInput,
  Label,
  Modal
} from '@components/index';
import {
  GROUP_MEMBER_MAX_AMOUNT,
  GROUP_MEMBER_MIN_AMOUNT
} from '@constants/generic';
import {
  AddFromMembersListItem,
  AmountChangeParams,
  FeeCategoryInputType
} from '@features/group-management/types';
import { FeeConfigurationType } from '@types';
import {
  getAmountErrorMessage,
  getMaxAmountErrorMessage,
  roundTo2
} from '@utils/generic';

interface DueAmountFieldProps {
  rowIndex: number;
  row: AddFromMembersListItem;
  isFeeCategoryEnabled: boolean;
  feeCategoryData: FeeConfigurationType[];
  onFeeCategorySave: (params: FeeConfigurationType[], index: number) => void;
  onAmountChange: (dueAmount: string, memberIndex: number) => void;
  customInputStyle?: string;
}

const DueAmountField = ({
  rowIndex,
  row,
  isFeeCategoryEnabled,
  feeCategoryData,
  onAmountChange,
  onFeeCategorySave,
  customInputStyle
}: DueAmountFieldProps) => {
  const { orgConfig } = useSelector((state: any) => state.user);

  const groupMemberMaxAmount =
    orgConfig?.group_member_amount_max || GROUP_MEMBER_MAX_AMOUNT;
  const groupMemberMinAmount =
    orgConfig?.group_member_amount_min || GROUP_MEMBER_MIN_AMOUNT;

  const [isFeePopupOpen, setIsFeePopupOpen] = useState(false);
  const [error, setError] = useState({ amount: '' });
  const [formattedFeeCategories, setFormattedFeeCategories] = useState<
    FeeCategoryInputType[]
  >([]);
  const [newFeeData, setNewFeeData] = useState<FeeCategoryInputType[]>([]);

  const handleCancel = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    setIsFeePopupOpen(false);
    if (formattedFeeCategories.length > 0) {
      setNewFeeData(formattedFeeCategories);
    } else {
      setNewFeeData(
        feeCategoryData.map((feeCategory) => ({
          category: feeCategory.category,
          amount: feeCategory.amount,
          isManuallyUpdated: false
        }))
      );
    }
  };

  const handleSave = () => {
    onFeeCategorySave(
      feeCategoryData.map((feeConfig, index) => ({
        ...feeConfig,
        amount: newFeeData[index].amount.toString(),
        isManuallyUpdated: newFeeData[index].isManuallyUpdated
      })),
      rowIndex
    );
    setFormattedFeeCategories(newFeeData);
    const totalAmount = roundTo2(
      newFeeData.reduce((sum, item) => sum + Number(item.amount), 0)
    );
    onAmountChange(Number(totalAmount)?.toString() || '', rowIndex);
    setIsFeePopupOpen(false);
  };

  const onAmountBlur = (value: string) => {
    if (value.length && Number(value) < 0) {
      setError({
        ...error,
        amount: 'Amount cannot be less than 0'
      });
    }
    if (
      value.length &&
      Number(value) !== 0 &&
      Number(value) < Number(groupMemberMinAmount)
    ) {
      setError({
        ...error,
        amount: getAmountErrorMessage(groupMemberMinAmount)
      });
    }
  };

  const onAmountClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (isFeeCategoryEnabled) {
      setIsFeePopupOpen(true);
    }
  };

  const handleAmountChange = (value: string) => {
    if (!isFeeCategoryEnabled) onAmountChange(value, rowIndex);
    setError({ ...error, amount: '' });
  };

  const calculateTotalAmount = (feeCategories: FeeCategoryInputType[]) =>
    roundTo2(
      feeCategories?.reduce((sum, category) => sum + Number(category.amount), 0)
    );

  const totalAmount = calculateTotalAmount(newFeeData);

  const handleCategoryAmountChange = ({ value, index }: AmountChangeParams) => {
    const updatedCategories = [...newFeeData];
    updatedCategories[index].amount = value;
    updatedCategories[index].isManuallyUpdated = true;
    setNewFeeData(updatedCategories);
    const currentTotalAmount = calculateTotalAmount(updatedCategories);

    if (
      currentTotalAmount !== 0 &&
      currentTotalAmount < Number(groupMemberMinAmount)
    ) {
      setError({
        ...error,
        amount: getAmountErrorMessage(groupMemberMinAmount)
      });
    } else if (currentTotalAmount > Number(groupMemberMaxAmount)) {
      setError({
        ...error,
        amount: getMaxAmountErrorMessage(groupMemberMaxAmount)
      });
    } else if (error.amount) {
      setError({ ...error, amount: '' });
    }
  };

  const getFeeConfigValue = (
    member: AddFromMembersListItem,
    categoryItem: any
  ) => {
    const category = member?.feeCategoryData?.find(
      (item) =>
        item.id === (categoryItem.branch_fee_category_id ?? categoryItem.id)
    );
    if (category?.isManuallyUpdated) {
      return category.value;
    }
    return categoryItem.amount || categoryItem.value || 0;
  };

  const isFeeConfigUpdated = (
    member: AddFromMembersListItem,
    categoryItem: any
  ) => {
    const category = member?.feeCategoryData?.find(
      (item) =>
        item.id === (categoryItem.branch_fee_category_id ?? categoryItem.id)
    );
    return !!category?.isManuallyUpdated;
  };

  const isSaveDisabled =
    !!error.amount ||
    (isFeeCategoryEnabled &&
      newFeeData.filter((item) => item.amount === '').length > 0);

  useEffect(() => {
    if (isFeeCategoryEnabled) {
      if (
        feeCategoryData &&
        row.feeCategoryData &&
        row.isAmountManuallyEdited
      ) {
        const formattedFeeCategoriesData = feeCategoryData.map(
          (feeCategory) => ({
            category: feeCategory.category,
            amount: getFeeConfigValue(row, feeCategory),
            isManuallyUpdated: isFeeConfigUpdated(row, feeCategory)
          })
        );
        setNewFeeData(formattedFeeCategoriesData);
        onAmountChange(
          roundTo2(
            formattedFeeCategoriesData?.reduce(
              (sum, item) => sum + Number(item.amount),
              0
            )
          ).toString() || '0',
          rowIndex
        );
      } else if (feeCategoryData) {
        setNewFeeData(
          feeCategoryData.map((feeCategory) => ({
            category: feeCategory.category,
            amount: feeCategory.amount,
            isManuallyUpdated: false
          }))
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(feeCategoryData), JSON.stringify(row.feeCategoryData)]);

  return (
    <>
      <AmountInput
        readOnly={isFeeCategoryEnabled}
        className="max-w-50"
        customInputStyle={customInputStyle}
        inputClassNames={`${isFeeCategoryEnabled ? 'cursor-pointer' : ''}`}
        value={row.dueAmount}
        onClick={(e) => onAmountClick(e)}
        onAmountChange={handleAmountChange}
        disabled={row.isAlreadyPresent}
        maxAmount={groupMemberMaxAmount}
        allowDecimals
        allowZero
        {...(!isFeeCategoryEnabled && {
          handleInputBlur: onAmountBlur,
          error: !!error.amount,
          errorMessage: error.amount
        })}
        role="presentation"
        onKeyDown={(e) => e.stopPropagation()}
      />
      {isFeePopupOpen && (
        <Modal
          isOpen={isFeePopupOpen}
          onCancel={handleCancel}
          isCloseIconRequired
          className="cursor-default p-5"
          closeIconClassName="!mr-0"
        >
          <h2 className="bg-grey20 pb-3 pt-1 text-xl font-medium">
            Edit Due Amount
          </h2>
          <div className="custom-scroll mr-[3px] max-h-125 space-y-6 overflow-y-auto pr-2.5">
            <Label label="Amount" className="!mb-0 mr-2" />
            <AmountInput
              readOnly
              value={totalAmount}
              placeholder="Enter Amount"
              onAmountChange={() => null}
              maxAmount={groupMemberMaxAmount}
              error={!!error.amount}
              errorMessage={error.amount}
              className="!mt-2"
            />
            {isFeeCategoryEnabled && (
              <FeeConfigInput
                feeCategories={newFeeData}
                onCategoryAmountChange={handleCategoryAmountChange}
                isWarningRequired
                titleStyle="text-sm font-semibold"
              />
            )}
          </div>
          <div className="flex w-full justify-between pt-2">
            <Button
              label="Cancel"
              handleClick={(e) => {
                e.stopPropagation();
                handleCancel();
              }}
              variant="secondary"
              size="medium"
              className="w-full"
            />
            <Button
              disabled={isSaveDisabled}
              label="Save"
              handleClick={(e) => {
                e.stopPropagation();
                handleSave();
              }}
              variant="primary"
              size="medium"
              className="ml-6 w-full"
            />
          </div>
        </Modal>
      )}
    </>
  );
};

export default DueAmountField;
